import { IncidentSummarySchema, ShowMoreState } from "../incidentSummary";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import { UserClaims } from "core/contexts/auth/auth";
import { ReactNode, useEffect, useState } from "react";

import {
  FlywheelIocsContainer,
  TagsButton,
  TextContainer,
  CommentsContainer,
  FeedbackStyle,
  PanelContainer,
  MarkdownContainer,
  ActionButtonsContainer,
} from "../incidentSummary.styled";

import Markdown from "markdown-to-jsx";
import {
  ContentCopy,
  FileDownloadDone,
  ThumbDownAlt,
  ThumbUpAlt,
} from "@mui/icons-material";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { UserInput } from "../incidentInput";
import {
  BootstrapDialog,
  ChatBotTextArea,
  ChatBotTextAreaDiv,
  FeedbackTextP,
  InnerDiv,
} from "modules/chat/components/chat.styled";
import { sendFeedbackToApi } from "modules/chat/services/chatService";
import IocRow from "../iocRow";
import IncidentComments from "../incidentComments";
import { useTheme } from "styled-components";
import { BootstrapDialogTitle } from "core/components/global/bootstrap/dialogTitle";

export interface FlywheelTableSummaryProps {
  incidentSummary: IncidentSummarySchema | undefined;
  showMore: ShowMoreState;
  handleClick: (string) => void;
  user: UserClaims | null;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

export const FlywheelTableSummary = ({
  incidentSummary,
  showMore,
  handleClick,
  user,
}: FlywheelTableSummaryProps) => {
  const [userPrompt, setUserPrompt] = useState("");
  const theme = useTheme();
  const { t } = useTranslation();

  const [iscopied, setIsCopied] = useState({
    report: false,
    mitre: false,
    cve: false,
  });
  const [copiedValue, setCopiedValue] = useState("");
  const [isLiked, setIsLiked] = useState({
    report: false,
    mitre: false,
    cve: false,
  });
  const [isDisliked, setIsDisliked] = useState({
    report: false,
    mitre: false,
    cve: false,
  });
  const [open, setOpen] = useState(false);
  const [isSubmiited, setIsSubmitted] = useState({
    report: false,
    mitre: false,
    cve: false,
  });
  const [feedbackText, setFeedbackText] = useState("");
  const [tabValue, setTabValue] = useState({ actions: 0, response: 0 });
  const [copiedText, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const copyText = async () => {
      if (copiedValue) {
        const success = await copyToClipboard(copiedValue);
        if (!success) {
          console.log(
            "Copy failed, no permission or clipboard support, update your browser permissions."
          );
        }
      }
    };

    copyText();
  }, [copiedValue]);

  /**
   * Either point to a new tab to show or point to no tab
   * 0 will show no sections, any other number with an assoicated
   * section will show that section
   * @param newValue
   */
  const handleTabChange = (newValue: number, type: string) => {
    if (type in tabValue) {
      if (newValue === tabValue[type]) {
        setTabValue((current) => ({
          ...current,
          [type]: 0,
        }));
      } else {
        setTabValue((current) => ({
          ...current,
          [type]: newValue,
        }));
      }
    }
  };

  const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{ width: "inherit" }}
        {...other}
      >
        {value === index && <PanelContainer>{children}</PanelContainer>}
      </div>
    );
  };

  const handleLikeClickOpen = (section) => {
    setOpen(true);
    setIsLiked({
      ...isLiked,
      [section]: true,
    });
  };

  const handleDislikeClickOpen = (section) => {
    setOpen(true);
    setIsDisliked({
      ...isDisliked,
      [section]: true,
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (section) => {
    setIsSubmitted({
      ...iscopied,
      [section]: true,
    });
    
    // sendFeedbackToApi(
    //   incidentSummary?.flywheelReport,
    //   isLiked,
    //   userPrompt,
    //   feedbackText
    // );
  };

  const handleChangeFeedBack = (e) => {
    const { value } = e.target;
    setFeedbackText(value);
  };

  // Define handlers
  const handleCopy = (section, text) => {
    setCopiedValue(text);
    setIsCopied({
      ...iscopied,
      [section]: true,
    });
  };

  return (
    <>
      {incidentSummary?.flywheelIocs ||
      incidentSummary?.flywheelCve ||
      incidentSummary?.flywheelMitre ||
      incidentSummary?.flywheelReport ? (
        <>
          <Box className="d-flex flex-column">
            <strong style={{ fontSize: theme.font.large }}>
              {t("INCIDENTS_PAGE.AI_RESPONSE")}:
            </strong>
            <TextContainer
              container
              spacing={2}
              className="d-flex flex-wrap flex-row pt-2"
            >
              <TagsButton
                onClick={() => handleTabChange(5, "response")}
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                IOCS
              </TagsButton>
              <TagsButton
                onClick={() => handleTabChange(2, "response")}
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                CVE
              </TagsButton>
              <TagsButton
                onClick={() => handleTabChange(3, "response")}
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                Mitre
              </TagsButton>
              <TagsButton
                onClick={() => handleTabChange(4, "response")}
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                Report
              </TagsButton>
            </TextContainer>
          </Box>

          {incidentSummary?.flywheelIocs &&
            incidentSummary?.flywheelIocs.length > 0 && (
              <>
                <TabPanel value={tabValue["response"]} index={5}>
                  <TableContainer
                    component={Paper}
                    elevation={4}
                    className="shadow-xl w-100"
                  >
                    <FlywheelIocsContainer>
                      <Table aria-label="flywheel-iocs">
                        <TableHead component={Paper} elevation={4}>
                          <TableRow>
                            <TableCell align="left">
                              {t("IOC_ROW.TIME")}
                            </TableCell>
                            <TableCell align="left">
                              {t("IOC_ROW.FROM")}
                            </TableCell>
                            <TableCell align="left">
                              {t("IOC_ROW.TYPE")}
                            </TableCell>
                            <TableCell align="left">
                              {t("IOC_ROW.FIRST_SEEN")}
                            </TableCell>
                            <TableCell align="left">
                              {t("IOC_ROW.RISK_SCORE")}
                            </TableCell>
                            <TableCell align="left">
                              {t("IOC_ROW.VALUE")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody component={Paper} elevation={2}>
                          {incidentSummary?.flywheelIocs.map(
                            (row, iocIndex) => (
                              <IocRow
                                t={t}
                                row={row}
                                index={iocIndex}
                                key={iocIndex}
                                showMore={showMore}
                                handleClick={handleClick}
                                theme={theme}
                              />
                            )
                          )}
                        </TableBody>
                      </Table>
                    </FlywheelIocsContainer>
                  </TableContainer>
                </TabPanel>
              </>
            )}

          {incidentSummary?.flywheelCve &&
            incidentSummary?.flywheelCve !== "" && (
              <TabPanel value={tabValue["response"]} index={2}>
                <MarkdownContainer className="mb-4">
                  <div className="position-relative">
                    <FeedbackStyle>
                      {!iscopied["cve"] ? (
                        <Tooltip title="Copy" arrow className="me-2">
                          <Fade in={true} timeout={1000}>
                            <ContentCopy
                              onClick={() =>
                                handleCopy("cve", incidentSummary?.flywheelCve)
                              }
                              className="ChatBot_ContentCopy"
                              color="secondary"
                              elevation={1}
                              fontSize="small"
                            />
                          </Fade>
                        </Tooltip>
                      ) : (
                        <Fade in={true} timeout={1000} className="me-2">
                          <FileDownloadDone
                            className="ChatBot_ContentCopy"
                            color="secondary"
                            elevation={1}
                            fontSize="small"
                          />
                        </Fade>
                      )}

                      {/* Feedback - Liked */}
                      {!isLiked["cve"] && !isDisliked["cve"] ? (
                        <Tooltip title="Like" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbUpOffAltIcon
                              onClick={() => handleLikeClickOpen("cve")}
                              
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isLiked["cve"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbUpAlt
                            
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}
                      {/* {Feedback - DisLiked} */}
                      {!isDisliked["cve"] && !isLiked["cve"] ? (
                        <Tooltip title="Dislike" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbDownOffAltIcon
                              onClick={() => handleDislikeClickOpen("cve")}
                              className="me-2"
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isDisliked["cve"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbDownAlt
                            className="me-2"
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}

                      {/* Code for Feedback dialog box  */}
                      <BootstrapDialog
                        onClose={handleClose}
                        aria-labelledby="customized-dialog-title"
                        open={open}
                      >
                        <div>
                          <BootstrapDialogTitle
                            id="customized-dialog-title"
                            onClose={handleClose}
                            customTheme={theme}
                          >
                            {isSubmiited["cve"]
                              ? ""
                              : "Provide additional feedback"}
                          </BootstrapDialogTitle>
                          <DialogContent dividers>
                            {!isSubmiited["cve"] ? (
                              <>
                                <ChatBotTextArea
                                  placeholder="Write your feedback..."
                                  value={feedbackText}
                                  onChange={handleChangeFeedBack}
                                />
                              </>
                            ) : (
                              <ChatBotTextAreaDiv>
                                <InnerDiv>
                                  <ThumbUpOffAltIcon />
                                  <FeedbackTextP>
                                    Thank you for your Feedback
                                  </FeedbackTextP>
                                </InnerDiv>
                              </ChatBotTextAreaDiv>
                            )}
                          </DialogContent>
                          <DialogActions>
                            {!isSubmiited["cve"] ? (
                              <Button
                                autoFocus
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!feedbackText}
                              >
                                Submit Feedback
                              </Button>
                            ) : (
                              <></>
                            )}
                          </DialogActions>
                        </div>
                      </BootstrapDialog>
                    </FeedbackStyle>
                  </div>
                  <Markdown>{incidentSummary.flywheelCve}</Markdown>
                </MarkdownContainer>
              </TabPanel>
            )}

          {incidentSummary?.flywheelMitre &&
            incidentSummary?.flywheelMitre !== "" && (
              <TabPanel value={tabValue["response"]} index={3}>
                <MarkdownContainer className="mb-4">
                  <div className="position-relative">
                    <FeedbackStyle>
                      {!iscopied["mitre"] ? (
                        <Tooltip title="Copy" arrow className="me-2">
                          <Fade in={true} timeout={1000}>
                            <ContentCopy
                              onClick={() =>
                                handleCopy(
                                  "mitre",
                                  incidentSummary?.flywheelMitre
                                )
                              }
                              className="ChatBot_ContentCopy"
                              color="secondary"
                              elevation={1}
                              fontSize="small"
                            />
                          </Fade>
                        </Tooltip>
                      ) : (
                        <Fade in={true} timeout={1000} className="me-2">
                          <FileDownloadDone
                            className="ChatBot_ContentCopy"
                            color="secondary"
                            elevation={1}
                            fontSize="small"
                          />
                        </Fade>
                      )}

                      {/* Feedback - Liked */}
                      {!isLiked["mitre"] && !isDisliked["mitre"] ? (
                        <Tooltip title="Like" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbUpOffAltIcon
                              onClick={() => handleLikeClickOpen("mitre")}
                              
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isLiked["mitre"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbUpAlt
                            
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}
                      {/* {Feedback - DisLiked} */}
                      {!isDisliked["mitre"] && !isLiked["mitre"] ? (
                        <Tooltip title="Dislike" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbDownOffAltIcon
                              onClick={() => handleDislikeClickOpen("mitre")}
                              className="me-2"
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isDisliked["mitre"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbDownAlt
                            className="me-2"
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}

                      {/* Code for Feedback dialog box  */}
                      <BootstrapDialog
                        onClose={handleClose}
                        aria-labelledby="customized-dialog-title"
                        open={open}
                      >
                        <div>
                          <BootstrapDialogTitle
                            id="customized-dialog-title"
                            onClose={handleClose}
                            customTheme={theme}
                          >
                            {isSubmiited["mitre"]
                              ? ""
                              : "Provide additional feedback"}
                          </BootstrapDialogTitle>
                          <DialogContent dividers>
                            {!isSubmiited["mitre"] ? (
                              <>
                                <ChatBotTextArea
                                  placeholder="Write your feedback..."
                                  value={feedbackText}
                                  onChange={handleChangeFeedBack}
                                />
                              </>
                            ) : (
                              <ChatBotTextAreaDiv>
                                <InnerDiv>
                                  <ThumbUpOffAltIcon />
                                  <FeedbackTextP>
                                    Thank you for your Feedback
                                  </FeedbackTextP>
                                </InnerDiv>
                              </ChatBotTextAreaDiv>
                            )}
                          </DialogContent>
                          <DialogActions>
                            {!isSubmiited["mitre"] ? (
                              <Button
                                autoFocus
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!feedbackText}
                              >
                                Submit Feedback
                              </Button>
                            ) : (
                              <></>
                            )}
                          </DialogActions>
                        </div>
                      </BootstrapDialog>
                    </FeedbackStyle>
                  </div>
                  <Markdown>{incidentSummary?.flywheelMitre}</Markdown>
                </MarkdownContainer>
              </TabPanel>
            )}

          {incidentSummary?.flywheelReport &&
            incidentSummary?.flywheelReport !== "" && (
              <TabPanel value={tabValue["response"]} index={4}>
                <MarkdownContainer className="mb-4">
                  <div className="position-relative">
                    <FeedbackStyle>
                      {!iscopied["report"] ? (
                        <Tooltip title="Copy" arrow className="me-2">
                          <Fade in={true} timeout={1000}>
                            <ContentCopy
                              onClick={() =>
                                handleCopy(
                                  "report",
                                  incidentSummary?.flywheelReport
                                )
                              }
                              className="ChatBot_ContentCopy"
                              color="secondary"
                              elevation={1}
                              fontSize="small"
                            />
                          </Fade>
                        </Tooltip>
                      ) : (
                        <Fade in={true} timeout={1000} className="me-2">
                          <FileDownloadDone
                            className="ChatBot_ContentCopy"
                            color="secondary"
                            elevation={1}
                            fontSize="small"
                          />
                        </Fade>
                      )}

                      {/* Feedback - Liked */}
                      {!isLiked["report"] && !isDisliked["report"] ? (
                        <Tooltip title="Like" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbUpOffAltIcon
                              onClick={() => handleLikeClickOpen("report")}
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isLiked["report"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbUpAlt
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}
                      {/* {Feedback - DisLiked} */}
                      {!isDisliked["report"] && !isLiked["report"] ? (
                        <Tooltip title="Dislike" arrow>
                          <Fade in={true} timeout={1000} className="me-2">
                            <ThumbDownOffAltIcon
                              onClick={() => handleDislikeClickOpen("report")}
                              className="me-2"
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          </Fade>
                        </Tooltip>
                      ) : isDisliked["report"] ? (
                        <Fade in={true} timeout={1000}>
                          <ThumbDownAlt
                            className="me-2"
                            color="secondary"
                            fontSize="small"
                            elevation={1}
                          />
                        </Fade>
                      ) : (
                        ""
                      )}

                      {/* Code for Feedback dialog box  */}
                      <BootstrapDialog
                        onClose={handleClose}
                        aria-labelledby="customized-dialog-title"
                        open={open}
                      >
                        <div>
                            <BootstrapDialogTitle
                            id="customized-dialog-title"
                            onClose={handleClose}
                            customTheme={theme}
                            >
                            {isSubmiited["report"]
                                ? ""
                                : "Provide additional feedback"}
                            </BootstrapDialogTitle>
                            <DialogContent dividers>
                            {!isSubmiited["report"] ? (
                                <>
                                <ChatBotTextArea
                                    placeholder="Write your feedback..."
                                    value={feedbackText}
                                    onChange={() => handleChangeFeedBack("report")}
                                />
                                </>
                            ) : (
                                <ChatBotTextAreaDiv>
                                <InnerDiv>
                                    <ThumbUpOffAltIcon />
                                    <FeedbackTextP>
                                    Thank you for your Feedback
                                    </FeedbackTextP>
                                </InnerDiv>
                                </ChatBotTextAreaDiv>
                            )}
                            </DialogContent>
                            <DialogActions>
                            {!isSubmiited["report"] ? (
                                <Button
                                autoFocus
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!feedbackText}
                                >
                                Submit Feedback
                                </Button>
                            ) : (
                                <></>
                            )}
                            </DialogActions>
                        </div>
                      </BootstrapDialog>
                    </FeedbackStyle>
                  </div>
                  <Markdown>{incidentSummary?.flywheelReport}</Markdown>
                </MarkdownContainer>
              </TabPanel>
            )}
        </>
      ) : (
        <>
          <Box className="d-flex flex-column">
            <strong style={{ fontSize: theme.font.large }}>
              {t("INCIDENTS_PAGE.AI_RESPONSE")}:
            </strong>
            <TextContainer
              container
              spacing={2}
              className="d-flex flex-wrap flex-row pt-2"
            >
              <TagsButton
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                IOCs
              </TagsButton>
              <TagsButton
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                CVE
              </TagsButton>
              <TagsButton
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                Mitre
              </TagsButton>
              <TagsButton
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: theme.colors.darkBlueBtn,
                  fontSize: theme.font.medium,
                }}
              >
                Report
              </TagsButton>
            </TextContainer>
          </Box>
        </>
      )}

      <CommentsContainer>
        <IncidentComments
          t={t}
          theme={theme}
          initialComments={incidentSummary?.comments || []}
          incidentId={incidentSummary?.id || ""}
          user={user}
        />
      </CommentsContainer>

      <ActionButtonsContainer>
        <strong style={{ fontSize: theme.font.large }}>
          {t("INCIDENTS_PAGE.ACTIONS")}:
        </strong>
        <TextContainer
          container
          spacing={2}
          className="d-flex flex-wrap flex-row pt-2"
        >
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.TRUE_POSITIVE")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.FALSE_POSITIVE")}
          </TagsButton>
          {/* <TagsButton onClick={() => console.log("")} variant="contained" className="p-3 m-2" style={{backgroundColor: theme.colors.darkBlueBtn, fontSize: theme.font.medium}}>
                    {t("INCIDENTS_PAGE.GENERATE_REMEDIATION")}
                </TagsButton> */}
          <TagsButton
            onClick={() => handleTabChange(1, "actions")}
            variant="contained"
            className="p-3 m-2"
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
            }}
          >
            {t("INCIDENTS_PAGE.DETECTION_RULE")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.GENERATE_TICKET")}
          </TagsButton>
        </TextContainer>

        {incidentSummary?.flywheelDetectionRule &&
          incidentSummary?.flywheelDetectionRule !== "" && (
            <TabPanel value={tabValue["actions"]} index={1}>
              <MarkdownContainer className="mb-4 mt-4">
                <Markdown>{incidentSummary.flywheelDetectionRule}</Markdown>
              </MarkdownContainer>
            </TabPanel>
          )}
      </ActionButtonsContainer>

      <ActionButtonsContainer className="my-3">
        <strong style={{ fontSize: theme.font.large }}>
          {t("INCIDENTS_PAGE.ACTIONS_OTHER")}:
        </strong>
        <TextContainer
          container
          spacing={2}
          className="d-flex flex-wrap flex-row pt-2"
        >
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.EXPLORE_IOCS")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.SEARCH_SIMILAR")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.MALWARE_ANALYSIS_CODE_EXPLAIN")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.LANGUAGE_TRANSLATION")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.SOC_KNOWLEDGE_BASE")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.SEVERITY_ESCALATION_RECOMMENDATION")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.TRAINING_UPSKILLING")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.SOAR_PLAYBOOKS")}
          </TagsButton>
          <TagsButton
            onClick={() => console.log("")}
            variant="contained"
            className="p-3 m-2"
            disabled={true}
            style={{
              backgroundColor: theme.colors.darkBlueBtn,
              fontSize: theme.font.medium,
              opacity: "0.5",
            }}
          >
            {t("INCIDENTS_PAGE.AI_SERVICE_DESK_SUPPORT")}
          </TagsButton>
        </TextContainer>
      </ActionButtonsContainer>

      <strong className="mt-3 mb-3 py-3" style={{ fontSize: theme.font.large }}>
        {t("INCIDENTS_PAGE.CONVERSE_WITH_AI_FLYWHEEL")}:
      </strong>

      <UserInput
        theme={theme}
        userPrompt={userPrompt}
        setUserPrompt={setUserPrompt}
      />

      <TextContainer container spacing={2} className="d-flex flex-column">
        <strong className="mb-3" style={{ fontSize: theme.font.large }}>
          {t("INCIDENTS_PAGE.AI_DISCLAMER_TITLE")}:
        </strong>
        <p className="word-break">
          <strong>{t("INCIDENTS_PAGE.PLEASE_NOTE")}: </strong>
          The information provided here has been generated by AI and, as such,
          may contain inaccuracies or &#39;hallucinations&#39;. We advise users
          to verify the accuracy of the information before relying on it for any
          critical decisions or actions.
        </p>
      </TextContainer>
    </>
  );
};

export default FlywheelTableSummary;
